import { hasAttendanceContractType, isTrainingContractType } from '@/utils/attendanceMode';

/**
 * @see https://umijs.org/docs/max/access#access
 */
export default function access(
  initialState:
    | {
        currentAccount?: API.CurrentAccount;
      }
    | undefined,
) {
  const { currentAccount } = initialState ?? {};
  const permissions = currentAccount?.permissions || [];
  const roles = currentAccount?.roles || [];
  const hasPerm = (perm: string) => permissions.includes(perm);
  const hasAnyPerm = (perms: string[]) => perms.some(hasPerm);
  const isAdmin = roles.includes('Admin');

  const canS002InitView = hasPerm('cbbm_s002:init:view');
  const canS003InitView = hasPerm('cbbm_s003:init:view');
  const canS004InitView = hasPerm('cbbm_s004:init:view');
  const canS005InitView = hasPerm('cbbm_s005:init:view');
  const canS006InitView = hasPerm('cbbm_s006:init:view');
  const canS007InitView = hasPerm('cbbm_s007:init:view');

  const canG001InitView = hasPerm('cbbm_g101:init:view');
  const canG002InitView = hasPerm('cbbm_g102:init:view');
  const canG201InitView = hasPerm('cbbm_g201:init:view');
  const canG202InitView = hasPerm('cbbm_g202:init:view');
  const canG301InitView = hasPerm('cbbm_g301:init:view');
  const canG301ApprovalView = hasPerm('cbbm_g301:approval:view');
  const canG301WorkflowView = hasPerm('cbbm_g301:workflow:view');
  const canG401InitView = hasPerm('cbbm_g401:init:view');

  const canHR101InitView = hasPerm('cbbm_hr101:init:view');
  const canHR102InitView = hasPerm('cbbm_hr102:init:view');
  const canHR103InitView = hasPerm('cbbm_hr103:init:view');
  const canHR104InitView = hasPerm('cbbm_hr104:init:view');
  const canHR104ManagementView = hasPerm('cbbm_hr104:management:view');

  const canDEV101InitView = hasPerm('cbbm_dev101:init:view');
  const canDEV102InitView = hasPerm('cbbm_dev102:init:view');
  const canDEV103InitView = hasPerm('cbbm_dev103:init:view');
  const canDEV104InitView = hasPerm('cbbm_dev104:init:view');

  const canE101InitView = hasPerm('cbbm_e101:init:view');
  const canE201InitView = hasPerm('cbbm_e201:init:view');
  const canE301InitView = hasPerm('cbbm_e301:init:view');

  const canACC100InitView = hasPerm('cbbm_acc100:init:view');
  const canACC101InitView = hasPerm('cbbm_acc101:init:view');
  const canACC102InitView = hasPerm('cbbm_acc102:init:view');
  const contractType = currentAccount?.contractType;
  const hasContractType = hasAttendanceContractType(contractType);
  const isTrainingAccount = isTrainingContractType(contractType);
  const canAttendanceInputOnsiteView =
    canG001InitView && (canG002InitView || (hasContractType && !isTrainingAccount));
  const canAttendanceInputTrainingView =
    canG001InitView && (canG002InitView || (hasContractType && isTrainingAccount));

  return {
    // Parent menu access is driven by visible child menu permissions.
    canS000InitView: hasAnyPerm([
      'cbbm_s002:init:view',
      'cbbm_s003:init:view',
      'cbbm_s004:init:view',
      'cbbm_s005:init:view',
      'cbbm_s006:init:view',
      'cbbm_s007:init:view',
    ]),
    canG000InitView: hasAnyPerm([
      'cbbm_g101:init:view',
      'cbbm_g102:init:view',
      'cbbm_g201:init:view',
      'cbbm_g202:init:view',
      'cbbm_g301:init:view',
      'cbbm_g301:approval:view',
      'cbbm_g301:workflow:view',
      'cbbm_g401:init:view',
    ]),
    canHR000InitView: hasAnyPerm([
      'cbbm_hr101:init:view',
      'cbbm_hr102:init:view',
      'cbbm_hr103:init:view',
      'cbbm_hr104:init:view',
      'cbbm_hr104:management:view',
    ]),
    canDEV000InitView: hasAnyPerm([
      'cbbm_dev101:init:view',
      'cbbm_dev102:init:view',
      'cbbm_dev103:init:view',
      'cbbm_dev104:init:view',
    ]),
    canE000InitView: hasAnyPerm([
      'cbbm_e101:init:view',
      'cbbm_e201:init:view',
      'cbbm_e301:init:view',
    ]),
    canACC000InitView: canACC100InitView || canACC101InitView || canACC102InitView,

    // Page level access.
    canS002InitView,
    canS003InitView,
    canS004InitView,
    canS005InitView,
    canS006InitView,
    canS007InitView,
    canG001InitView,
    canG002InitView,
    canAttendanceListOnsiteView: canG002InitView,
    canAttendanceListTrainingView: canG002InitView,
    canAttendanceInputOnsiteView,
    canAttendanceInputTrainingView,
    canG201InitView,
    canG202InitView,
    canG301InitView,
    canG301ApprovalView,
    canG301WorkflowView,
    canG401InitView,
    canHR101InitView,
    canHR102InitView,
    canHR102UndefinedView: canHR102InitView,
    canHR103InitView,
    canHR104InitView,
    canHR104ManagementView,
    canDEV101InitView,
    canDEV102InitView,
    canDEV103InitView,
    canDEV104InitView,
    canE101InitView,
    canE201InitView,
    canE301InitView,
    canACC100InitView,
    canACC101InitView,
    canACC102InitView,
    canACC102EntryUpdate: canACC102InitView && hasPerm('cbbm_acc102:entry:update'),

    // Role management.
    canS002RoleView: isAdmin || hasPerm('cbbm_s002:role:search'),
    canS002RoleCreate: isAdmin || hasPerm('cbbm_s002:role:create'),
    canS002RoleUpdate: isAdmin || hasPerm('cbbm_s002:role:update'),
    canS002RoleDelete: isAdmin || hasPerm('cbbm_s002:role:delete'),
    canS002RoleAuthView: isAdmin || hasPerm('cbbm_s002:role_auth:view'),
    canS002RoleAuthUpdate: isAdmin || hasPerm('cbbm_s002:role_auth:update'),

    // Page authorization management.
    canS003PageView: hasPerm('cbbm_s003:page:search'),
    canS003PageAuthUView: hasPerm('cbbm_s003:page_auth:view'),
    canS003OperAuthUpdate: hasPerm('cbbm_s003:page_auth:update'),

    // Account management.
    canS004AccountCreate: hasPerm('cbbm_s004:account:create'),
    canS004AccountView: hasPerm('cbbm_s004:account:search'),
    canS004PasswordUpdate: hasPerm('cbbm_s004:password:update'),
    canS004AccountUpdate: hasPerm('cbbm_s004:account:update'),
    canS004AccountDelete: hasPerm('cbbm_s004:account:delete'),
    canS004RoleSetView: hasPerm('cbbm_s004:role_set:view'),
    canS004RoleSetUpdate: hasPerm('cbbm_s004:role_set:update'),

    // Profile settings.
    canS005ProfileCreate: hasPerm('cbbm_s005:profile:create'),
    canS005ProfileUpdate: hasPerm('cbbm_s005:profile:update'),
    canS005ProfilePasswordUpdate: hasPerm('cbbm_s005:profile:password-update'),

    // Release and feedback management.
    canS006ReleaseCreate: hasPerm('cbbm_s006:release:create'),
    canS006ReleaseUpdate: hasPerm('cbbm_s006:release:update'),
    canS006ReleaseDelete: hasPerm('cbbm_s006:release:delete'),
    canS006FeedbackCreate: hasPerm('cbbm_s006:feedback:create'),
    canS006FeedbackUpdate: hasPerm('cbbm_s006:feedback:update'),
    canS006FeedbackAttachmentUpload: hasPerm('cbbm_s006:feedback:attachment:upload'),

    // Internal notices.
    canS007NoticeCreate: hasPerm('cbbm_s007:notice:create'),
    canS007NoticeUpdate: hasPerm('cbbm_s007:notice:update'),
    canS007NoticeDelete: hasPerm('cbbm_s007:notice:delete'),

    // General affairs.
    canG001InitUpdate: hasPerm('cbbm_g101:attendance:update'),
    canG001FileExport: hasPerm('cbbm_g101:attendance:export'),
    canG002InitSearch: hasPerm('cbbm_g102:attendance-list:search'),
    canG002FileExport: hasPerm('cbbm_g102:attendance:export'),
    canG002AttendanceApprove: hasPerm('cbbm_g102:attendance:approve'),
    canG002AttendanceReject: hasPerm('cbbm_g102:attendance:reject'),
    canG002AttendanceTargetUpdate: hasPerm('cbbm_g102:attendance-target:update'),
    canG201CommuteUpload: hasPerm('cbbm_g201:commute:upload'),
    canG201CommuteDelete: hasPerm('cbbm_g201:commute:delete'),
    canG201CommuteCommit: hasPerm('cbbm_g201:commute:commit'),
    canG201CommuteApprove: hasPerm('cbbm_g201:commute:approve'),
    canG202CommuteListSearch: hasPerm('cbbm_g202:commute-list:search'),
    canG301RequestCreate: hasPerm('cbbm_g301:request:create'),
    canG301RequestUpdate: hasPerm('cbbm_g301:request:update'),
    canG301RequestSubmit: hasPerm('cbbm_g301:request:submit'),
    canG301RequestCancel: hasPerm('cbbm_g301:request:cancel'),
    canG301ApprovalApprove: hasPerm('cbbm_g301:approval:approve'),
    canG301WorkflowUpdate: hasPerm('cbbm_g301:workflow:update'),
    canG401AssetCreate: hasPerm('cbbm_g401:asset:create'),
    canG401AssetUpdate: hasPerm('cbbm_g401:asset:update'),
    canG401AssetDelete: hasPerm('cbbm_g401:asset:delete'),
    canG401CategoryUpdate: hasPerm('cbbm_g401:category:update'),

    // Human resources.
    canHR101EmployeeUpdate: hasPerm('cbbm_hr101:employee:update'),
    canHR102Init: hasPerm('cbbm_hr102:init'),
    canHR102Search: hasPerm('cbbm_hr102:search'),
    canHR103InterviewCreate: hasPerm('cbbm_hr103:interview:create'),
    canHR103InterviewUpdate: hasPerm('cbbm_hr103:interview:update'),
    canHR103InterviewDelete: hasPerm('cbbm_hr103:interview:delete'),
    canHR103InterviewAccountIssue: hasPerm('cbbm_hr103:interview-account:issue'),
    canHR104RequestCreate: hasPerm('cbbm_hr104:request:create'),
    canHR104RequestUpdate: hasPerm('cbbm_hr104:request:update'),
    canHR104RequestSubmit: hasPerm('cbbm_hr104:request:submit'),
    canHR104RequestCancel: hasPerm('cbbm_hr104:request:cancel'),
    canHR104RequestDelete: hasPerm('cbbm_hr104:request:delete'),
    canHR104RequestDownload: hasPerm('cbbm_hr104:request:download'),
    canHR104RequestApprove: hasPerm('cbbm_hr104:request:approve'),
    canHR104RequestReturn: hasPerm('cbbm_hr104:request:return'),
    canHR104RequestPrepare: hasPerm('cbbm_hr104:request:prepare'),
    canHR104RequestUpload: hasPerm('cbbm_hr104:request:upload'),
    canHR104RequestComplete: hasPerm('cbbm_hr104:request:complete'),
    canHR104ManagementCancel: hasPerm('cbbm_hr104:management:cancel'),
    canHR104TypeManage: hasPerm('cbbm_hr104:type:manage'),
    canHR104TemplateManage: hasPerm('cbbm_hr104:template:manage'),
    canHR104StampManage: hasPerm('cbbm_hr104:stamp:manage'),
    canHR104RequestGenerate: hasPerm('cbbm_hr104:request:generate'),
    canHR104RequestRedeliver: hasPerm('cbbm_hr104:request:redeliver'),

    // Development.
    canDEV101ProgressSearch: hasPerm('cbbm_dev101:progress:search'),
    canDEV101ProgressMediumQaAnswerSearch: hasPerm('cbbm_dev101:progress-medium-qa-answer:search'),
    canDEV101ProgressTaskUpdate: hasPerm('cbbm_dev101:progress-task:update'),
    canDEV101ProgressRemarkUpdate: hasPerm('cbbm_dev101:progress-remark:update'),
    canDEV101ProgressCalculateUpdate: hasPerm('cbbm_dev101:progress-calculate:update'),
    canDEV101ProgressCalendarSearch: hasPerm('cbbm_dev101:progress-calendar:search'),
    canDEV101ProgressCalendarUpdate: hasPerm('cbbm_dev101:progress-calendar:update'),
    canDEV102LargeSearch: hasPerm('cbbm_dev102:large:search'),
    canDEV102LargeDetail: hasPerm('cbbm_dev102:large:detail'),
    canDEV102LargeCreate: hasPerm('cbbm_dev102:large:create'),
    canDEV102LargeUpdate: hasPerm('cbbm_dev102:large:update'),
    canDEV102LargeDelete: hasPerm('cbbm_dev102:large:delete'),
    canDEV102LargeMediumSearch: hasPerm('cbbm_dev102:large-medium:search'),
    canDEV102LargeMediumUpdate: hasPerm('cbbm_dev102:large-medium:update'),
    canDEV102MediumSearch: hasPerm('cbbm_dev102:medium:search'),
    canDEV102MediumCreate: hasPerm('cbbm_dev102:medium:create'),
    canDEV102MediumUpdate: hasPerm('cbbm_dev102:medium:update'),
    canDEV102MediumDelete: hasPerm('cbbm_dev102:medium:delete'),
    canDEV102SmallSearch: hasPerm('cbbm_dev102:small:search'),
    canDEV102SmallCreate: hasPerm('cbbm_dev102:small:create'),
    canDEV102SmallUpdate: hasPerm('cbbm_dev102:small:update'),
    canDEV102SmallDelete: hasPerm('cbbm_dev102:small:delete'),
    canDEV102SmallQaSearch: hasPerm('cbbm_dev102:small-qa:search'),
    canDEV102SmallQaCreate: hasPerm('cbbm_dev102:small-qa:create'),
    canDEV102SmallQaUpdate: hasPerm('cbbm_dev102:small-qa:update'),
    canDEV102SmallQaDelete: hasPerm('cbbm_dev102:small-qa:delete'),
    canDEV102MediumQaSearch: hasPerm('cbbm_dev102:medium-qa:search'),
    canDEV102MediumQaCreate: hasPerm('cbbm_dev102:medium-qa:create'),
    canDEV102MediumQaUpdate: hasPerm('cbbm_dev102:medium-qa:update'),
    canDEV102MediumQaDelete: hasPerm('cbbm_dev102:medium-qa:delete'),
    canDEV103ProgressTaskSearch: hasPerm('cbbm_dev103:progress-task:search'),
    canDEV103MediumQaAnswerSearch: hasPerm('cbbm_dev103:medium-qa-answer:search'),
    canDEV103MediumQaAnswerCreate: hasPerm('cbbm_dev103:medium-qa-answer:create'),
    canDEV103MediumQaAnswerUpdate: hasPerm('cbbm_dev103:medium-qa-answer:update'),
    canDEV103SmallQaAnswerSearch: hasPerm('cbbm_dev103:small-qa-answer:search'),
    canDEV103SmallQaAnswerCreate: hasPerm('cbbm_dev103:small-qa-answer:create'),
    canDEV103SmallQaAnswerUpdate: hasPerm('cbbm_dev103:small-qa-answer:update'),
    canDEV104LinkGenerate: hasPerm('cbbm_dev104:link:generate'),

    // Sales.
    canE101CompanyCreate: hasPerm('cbbm_e101:company:create'),
    canE101EmployeeCreate: hasPerm('cbbm_e101:employee:create'),
    canE201EmployeeSearch: hasPerm('cbbm_e201:employee:search'),
    canE201ProjectCreate: hasPerm('cbbm_e201:project:create'),
    canE201ProjectUpdate: hasPerm('cbbm_e201:project:update'),
    canE201ProjectDelete: hasPerm('cbbm_e201:project:delete'),
    canE301CompanySearch: hasPerm('cbbm_e301:company:search'),
    canE301InvoiceCreate: hasPerm('cbbm_e301:invoice:create'),
    canE301InvoiceExport: hasPerm('cbbm_e301:invoice:export'),
    canE301PaymentUpdate: hasPerm('cbbm_e301:payment:update'),

    // Accounting.
    canACC101SalaryUpdate: hasPerm('cbbm_acc101:salary:update'),
    canACC101RaiseConfirm: hasPerm('cbbm_acc101:raise:confirm'),
  };
}
